import { fitFixedDurationInBounds } from '@app/core/range';
import { useEffect, useRef } from 'react';
import { getRangeForSegments } from '../../helpers/get-range-for-segments';
import { hasPartialSegmentCoverage } from '../../helpers/has-partial-segment-coverage';
import {
  useFocusRange,
  useSetFocus,
  useStreamUserDefinedRange,
} from '../../stores/stream-screen.store';
import { useStreamSegmentsList } from './useStreamSegmentsQuery';

/**
 * When returned segments do not fully cover the user range, shifts the current focus
 * window so it fits within the available segment coverage (once per user range load).
 */
export function useAdjustFocusForPartialSegmentCoverage() {
  const segments = useStreamSegmentsList();
  const setFocus = useSetFocus();
  const focusRange = useFocusRange();
  const userRange = useStreamUserDefinedRange();
  const focusAdjustedForRangeRef = useRef<string | null>(null);
  const focusRangeRef = useRef(focusRange);
  focusRangeRef.current = focusRange;

  useEffect(() => {
    if (!segments.length || userRange.isLoading) {
      return;
    }

    const userRangeKey = `${userRange.from}-${userRange.to}`;
    if (focusAdjustedForRangeRef.current === userRangeKey) {
      return;
    }

    const segmentsRange = getRangeForSegments(segments);
    if (segmentsRange.from === 0 && segmentsRange.to === 0) {
      return;
    }

    focusAdjustedForRangeRef.current = userRangeKey;

    if (!hasPartialSegmentCoverage(userRange, segmentsRange)) {
      return;
    }

    const currentFocus = focusRangeRef.current;
    const focusDurationMs = currentFocus.to - currentFocus.from;
    const adjustedFocus = fitFixedDurationInBounds({
      anchorTs: currentFocus.from,
      durationMs: focusDurationMs,
      bounds: segmentsRange,
      anchor: 'start',
    });

    if (adjustedFocus.from !== currentFocus.from || adjustedFocus.to !== currentFocus.to) {
      setFocus(adjustedFocus);
    }
  }, [segments, userRange.from, userRange.to, userRange.isLoading, setFocus]);
}
